import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowRight, 
  CheckCircle, 
  Award,
  Briefcase,
  Target,
  Lightbulb,
  TrendingUp,
  Mail
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AboutPage = () => {
  const [content, setContent] = useState(null);

  // Default content
  const defaults = {
    badge_text: '40+ Years of Excellence',
    page_title: 'About SPT Digital Marketing',
    page_subtitle: 'Enterprise-level marketing expertise with a personal, collaborative approach — helping clients sharpen their message, strengthen their brand, and execute marketing that actually moves the needle.',
    founder_name: 'Channing Hoyle',
    founder_title: 'Founder, SPT Digital Marketing',
    founder_image: '',
    founder_bio: `<p>Channing Hoyle is the founder of SPT Digital Marketing, bringing more than <strong>40 years of hands-on experience</strong> in brand building, creative direction, and full-scale marketing leadership.</p>
<p>After retiring as the <strong>Senior Director of Marketing Communications for VELUX North America</strong>, Channing launched SPT to help businesses elevate their marketing with the same strategic discipline, creative excellence, and operational precision he brought to some of the most recognized brands in the home improvement industry.</p>
<p>Throughout his career, Channing has led large in-house creative teams, managed multi-million dollar budgets, and built integrated marketing programs that consistently drove category growth, brand awareness, and measurable sales results.</p>`,
    founder_quote: 'At SPT Digital Marketing, we blend enterprise-level marketing experience with a personal, collaborative approach — helping clients sharpen their message, strengthen their brand, and execute marketing that actually moves the needle.',
    stats: [
      { value: '40+', label: 'Years Experience' },
      { value: '24+', label: 'Years at VELUX' },
      { value: '$M+', label: 'Budgets Managed' },
      { value: '12+', label: 'Team Led' }
    ],
    expertise: [
      { title: 'Brand Strategy', description: 'Strategic brand positioning that sets you apart from the competition' },
      { title: 'Digital Marketing', description: 'Full-funnel marketing programs that drive measurable results' },
      { title: 'Consumer & Trade Advertising', description: 'Integrated campaigns across all channels' },
      { title: 'Influencer Programs', description: 'Strategic partnerships that amplify your brand reach' },
      { title: 'Product Placement', description: 'Getting your products in front of the right audiences' },
      { title: 'Web Design & Development', description: 'Custom websites and web applications' },
      { title: 'Paid Search & SEO', description: 'Driving qualified traffic and leads' },
      { title: 'Event Strategy', description: 'Memorable experiences that connect brands with audiences' }
    ],
    experience: [
      {
        role: 'Senior Director of Marketing Communications',
        company: 'VELUX North America',
        duration: '24+ Years',
        highlights: [
          'Oversaw national consumer and trade advertising',
          'Led digital strategy and influencer programs',
          'Managed 12-person internal creative team',
          'Developed integrated campaigns generating qualified leads'
        ]
      },
      {
        role: 'Manager, Creative Services',
        company: 'Cooper Hand Tools',
        duration: 'Previous Role',
        highlights: [
          'Managed multi-million dollar creative services budget',
          'Led 11-person team for catalogs, packaging (40,000 SKUs)',
          'Oversaw co-op advertising and trade shows',
          'Supported brands including Crescent, Lufkin, Nicholson'
        ]
      },
      {
        role: 'Creative Director',
        company: 'McDaniel & Hoyle Communications',
        duration: 'Previous Role',
        highlights: [
          'Led design for literature, packaging, merchandisers',
          'Clients: John Deere/Homelite, Rubbermaid, Freud',
          'Logo development and brand identity systems',
          'Full catalog design and production'
        ]
      },
      {
        role: 'Art Director',
        company: 'Robert Bosch Tool Corporation',
        duration: 'Previous Role',
        highlights: [
          'Managed 7-person creative team',
          'Produced domestic and international packaging (25,000 SKUs)',
          'Vermont American and private label brands',
          'Merchandising and literature systems'
        ]
      }
    ]
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/public/site-content/about`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent(response.data);
        }
      } catch (err) {
        console.log('Using default content');
      }
    };
    fetchContent();
  }, []);

  // Merge CMS content with defaults
  const c = { ...defaults, ...content };
  const stats = (content?.stats && content.stats.length > 0) ? content.stats : defaults.stats;
  const expertise = (content?.expertise && content.expertise.length > 0) ? content.expertise : defaults.expertise;
  
  // Parse experience - handle both string highlights and array highlights
  const experience = (content?.experience && content.experience.length > 0) 
    ? content.experience.map(exp => ({
        ...exp,
        highlights: typeof exp.highlights === 'string' 
          ? exp.highlights.split('\n').filter(h => h.trim())
          : exp.highlights || []
      }))
    : defaults.experience;

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section 
        className="pt-40 pb-20 lg:pt-48 lg:pb-24"
        style={{ background: `linear-gradient(to bottom right, var(--color-hero-bg-from, #F8FAFC), white, var(--color-hero-bg-to, #EFF6FF))` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
            >
              <Award className="w-4 h-4" />
              {c.badge_text}
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              {c.page_title}
            </h1>
            <p 
              className="text-xl leading-relaxed"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              {c.page_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ color: 'var(--color-heading-text, #0F172A)' }}
              >
                Meet {c.founder_name}
              </h2>
              <div 
                className="prose prose-lg space-y-6"
                style={{ color: 'var(--color-body-text, #475569)' }}
                dangerouslySetInnerHTML={{ __html: c.founder_bio }}
              />
              
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    style={{ 
                      backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                      color: 'var(--color-button-text, #FFFFFF)',
                      borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                    }}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Get in Touch
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button 
                    size="lg" 
                    variant="outline"
                    style={{ 
                      borderColor: 'var(--color-muted-text, #94A3B8)',
                      borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                    }}
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="space-y-6">
              <Card 
                className="border-0 shadow-xl text-white"
                style={{ background: `linear-gradient(to bottom right, var(--color-primary, #1F3C4F), var(--color-primary-hover, #152a38))` }}
              >
                <CardContent className="p-8">
                  <blockquote className="text-xl italic leading-relaxed mb-6">
                    "{c.founder_quote}"
                  </blockquote>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-xl font-bold">{c.founder_name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div>
                      <div className="font-semibold">{c.founder_name}</div>
                      <div className="text-white/70 text-sm">{c.founder_title}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, idx) => (
                  <div 
                    key={idx} 
                    className="text-center p-6 rounded-2xl"
                    style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
                  >
                    <div 
                      className="text-4xl font-bold mb-2"
                      style={{ color: 'var(--color-primary, #1F3C4F)' }}
                    >{stat.value}</div>
                    <div 
                      className="text-sm"
                      style={{ color: 'var(--color-body-text, #475569)' }}
                    >{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Areas of Expertise
            </h2>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              Decades of experience across the full spectrum of marketing disciplines
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.map((item, idx) => (
              <Card 
                key={idx} 
                className="border-0 shadow-md hover:shadow-lg transition-shadow"
                style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}
              >
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 mb-4" style={{ color: 'var(--color-primary, #1F3C4F)' }} />
                  <h3 
                    className="font-bold mb-2"
                    style={{ color: 'var(--color-heading-text, #0F172A)' }}
                  >{item.title}</h3>
                  <p 
                    className="text-sm"
                    style={{ color: 'var(--color-body-text, #475569)' }}
                  >{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Career Timeline */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Professional Experience
            </h2>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              A track record of success across leading brands and industries
            </p>
          </div>
          
          <div className="space-y-8">
            {experience.map((career, idx) => (
              <Card key={idx} className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid md:grid-cols-3">
                    <div 
                      className="p-8 text-white"
                      style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}
                    >
                      <Briefcase className="w-8 h-8 mb-4 opacity-80" />
                      <h3 className="text-xl font-bold mb-2">{career.role}</h3>
                      <p className="text-white/80 mb-1">{career.company}</p>
                      <p className="text-white/60 text-sm">{career.duration}</p>
                    </div>
                    <div className="p-8 md:col-span-2">
                      <h4 
                        className="font-semibold mb-4"
                        style={{ color: 'var(--color-heading-text, #0F172A)' }}
                      >Key Accomplishments</h4>
                      <ul className="space-y-3">
                        {career.highlights.map((highlight, hIdx) => (
                          <li key={hIdx} className="flex items-start gap-3">
                            <CheckCircle 
                              className="w-5 h-5 flex-shrink-0 mt-0.5" 
                              style={{ color: 'var(--color-primary, #1F3C4F)' }}
                            />
                            <span style={{ color: 'var(--color-body-text, #475569)' }}>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Our Approach
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg text-center" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
              <CardContent className="p-8">
                <div 
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                >
                  <Target className="w-8 h-8" />
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ color: 'var(--color-heading-text, #0F172A)' }}
                >Strategic Discipline</h3>
                <p style={{ color: 'var(--color-body-text, #475569)' }}>
                  Every decision is backed by data, experience, and a clear understanding of your business goals.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg text-center" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
              <CardContent className="p-8">
                <div 
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                >
                  <Lightbulb className="w-8 h-8" />
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ color: 'var(--color-heading-text, #0F172A)' }}
                >Creative Excellence</h3>
                <p style={{ color: 'var(--color-body-text, #475569)' }}>
                  Decades of creative leadership ensuring your brand stands out with distinction and purpose.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg text-center" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
              <CardContent className="p-8">
                <div 
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                >
                  <TrendingUp className="w-8 h-8" />
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ color: 'var(--color-heading-text, #0F172A)' }}
                >Measurable Results</h3>
                <p style={{ color: 'var(--color-body-text, #475569)' }}>
                  We focus on marketing that delivers real business impact — growth you can see and measure.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you sharpen your message, strengthen your brand, 
            and achieve measurable results.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6"
              style={{ 
                backgroundColor: 'var(--color-button-secondary-bg, #FFFFFF)',
                color: 'var(--color-primary, #1F3C4F)',
                borderRadius: 'var(--btn-computed-radius, 0.5rem)'
              }}
            >
              <Mail className="w-5 h-5 mr-2" />
              Start a Conversation
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default AboutPage;
